import {
  BarChart3,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  IndianRupee,
  Landmark,
  Smartphone,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import { CardSkeleton, SkeletonBlock, TableSkeleton } from "@/components/ui/Skeleton";
import StatCard from "@/components/ui/StatCard";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { MandateStatus } from "@/types/mandate";
import { MONTHS, RANGES, formatINR } from "@/pages/admin/data";
import DonutChart, { type DonutSegment } from "@/pages/retailer/dashboard/components/DonutChart";
import VolumeBarChart, { type BarPoint } from "./components/VolumeBarChart";
import BankBarChart, { type BankBar } from "./components/BankBarChart";
import { adminService } from "@/services/apiServices";
import {
  bankRows,
  collectionRows,
  collectionRateColor,
  inRange,
  initials,
  modeRows,
  monthlyRows,
  monthLabel,
  retailerRows,
  successBarColor,
  type CollectionRow,
} from "./data";

const DONUT_ORDER: MandateStatus[] = [
  "ACTIVE",
  "PENDING_AUTH",
  "CREATED",
  "COMPLETED",
  "TERMINATED",
  "CANCELLED",
  "PAUSED",
  "HALTED",
];

const DONUT_COLORS: Record<MandateStatus, string> = {
  ACTIVE: "#059669",
  PENDING_AUTH: "#D97706",
  CREATED: "#0284C7",
  COMPLETED: "#1E40AF",
  TERMINATED: "#DC2626",
  CANCELLED: "#475569",
  PAUSED: "#D97706",
  HALTED: "#DC2626",
};

const STATUS_LABELS: Record<MandateStatus, string> = {
  ACTIVE: "Active",
  PENDING_AUTH: "Pending Auth",
  CREATED: "Created",
  COMPLETED: "Completed",
  TERMINATED: "Terminated",
  CANCELLED: "Cancelled",
  PAUSED: "Paused",
  HALTED: "Halted",
};

const TABS = ["Summary", "By Retailer", "By Collection", "By Bank", "By Auth Mode"];

const MODE_META: { mode: string; label: string; icon: LucideIcon }[] = [
  { mode: "NACH", label: "eNACH", icon: Landmark },
  { mode: "UPI_AUTO_PAY", label: "UPI Autopay", icon: Smartphone },
];

type RetailerSortKey = "shopName" | "total" | "activated" | "pending" | "failedRejected" | "totalValue" | "successRate";

function escapeCsvValue(val: unknown): string {
  const s = String(val ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(data: (string | number)[][], filename: string) {
  const csvContent = data.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function AdminReports() {
  return (
    <ToastProvider>
      <ReportsInner />
    </ToastProvider>
  );
}

function ReportsInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [dbMandates, setDbMandates] = useState<any[]>([]);
  const [dbRetailers, setDbRetailers] = useState<any[]>([]);
  const [dbInstallments, setDbInstallments] = useState<any[]>([]);
  const [range, setRange] = useState("Last 30 Days");
  const [rangeOpen, setRangeOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [activeTab, setActiveTab] = useState("Summary");
  const [sortKey, setSortKey] = useState<RetailerSortKey>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminService
      .getReports(range, customFrom, customTo)
      .then((res: any) => {
        if (!active) return;
        setDbMandates(res?.data?.mandates ?? []);
        setDbRetailers(res?.data?.retailers ?? []);
        setDbInstallments(res?.data?.installments ?? []);
      })
      .catch((err) => {
        console.warn("Reports fetch error:", err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [range, customFrom, customTo]);

  useEffect(() => {
    if (!rangeOpen) return;
    const handler = () => setRangeOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [rangeOpen]);

  useEffect(() => {
    if (!downloadOpen) return;
    const handler = () => setDownloadOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [downloadOpen]);

  const filtered = useMemo(
    () => dbMandates.filter((m) => inRange(m, range, customFrom, customTo)),
    [dbMandates, range, customFrom, customTo]
  );

  // ---- Summary tab computations ----
  const summary = useMemo(() => {
    const total = filtered.length;
    const activated = filtered.filter((m) => m.status === "ACTIVE").length;
    const value = filtered.filter((m) => m.status === "ACTIVE").reduce((s, m) => s + Number(m.amount || 0), 0);
    const successRate = total ? ((activated / total) * 100).toFixed(1) : "0.0";
    return { total, activated, value, successRate };
  }, [filtered]);

  const weekly = useMemo(() => {
    const points: BarPoint[] = [];
    const today = new Date();
    for (let i = 11; i >= 0; i -= 1) {
      const end = new Date(today);
      end.setDate(today.getDate() - i * 7);
      end.setHours(23, 59, 59, 999);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      let count = 0;
      filtered.forEach((m) => {
        const d = new Date(m.createdAt).getTime();
        if (d >= start.getTime() && d <= end.getTime()) count += 1;
      });
      points.push({ label: `${start.getDate()} ${MONTHS[start.getMonth()]}`, value: count });
    }
    return points;
  }, [filtered]);

  const segments: DonutSegment[] = useMemo(() => {
    const counts = new Map<MandateStatus, number>();
    filtered.forEach((m) => counts.set(m.status, (counts.get(m.status) ?? 0) + 1));
    return DONUT_ORDER.filter((s) => (counts.get(s) ?? 0) > 0).map((s) => ({
      label: s,
      value: counts.get(s)!,
      color: DONUT_COLORS[s],
    }));
  }, [filtered]);

  const months = useMemo(() => {
    const arr: string[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      arr.push(monthLabel(new Date(now.getFullYear(), now.getMonth() - i, 1)));
    }
    return arr;
  }, []);
  const monthly = useMemo(() => monthlyRows(filtered, months), [filtered, months]);

  // ---- Retailer tab ----
  const retailerRowsData = useMemo(() => retailerRows(filtered, dbRetailers), [filtered, dbRetailers]);
  const sortedRetailers = useMemo(() => {
    const arr = [...retailerRowsData];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [retailerRowsData, sortKey, sortDir]);
  const retailerTotals = useMemo(
    () => ({
      total: retailerRowsData.reduce((s, r) => s + r.total, 0),
      activated: retailerRowsData.reduce((s, r) => s + r.activated, 0),
      pending: retailerRowsData.reduce((s, r) => s + r.pending, 0),
      failedRejected: retailerRowsData.reduce((s, r) => s + r.failedRejected, 0),
      value: retailerRowsData.reduce((s, r) => s + r.totalValue, 0),
    }),
    [retailerRowsData]
  );

  // ---- Bank tab ----
  const bankRowsData = useMemo(() => bankRows(filtered), [filtered]);
  const bankChart: BankBar[] = useMemo(
    () => bankRowsData.map((b) => ({ bank: b.bank, count: b.total })),
    [bankRowsData]
  );
  const bankBySuccess = useMemo(
    () => [...bankRowsData].sort((a, b) => b.successRate - a.successRate),
    [bankRowsData]
  );

  // ---- Auth mode tab ----
  const modeRowsData = useMemo(() => modeRows(filtered), [filtered]);
  const modeByMode = useMemo(() => {
    const map = new Map(modeRowsData.map((m) => [m.mode, m]));
    return MODE_META.map((mm) => map.get(mm.mode));
  }, [modeRowsData]);

  function toggleSort(key: RetailerSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const sortArrow = (key: RetailerSortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  function exportReportAsCsv() {
    setDownloadOpen(false);
    let headers: string[] = [];
    let rows: (string | number)[][] = [];
    const filenamePrefix = `report-${activeTab.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`;

    if (activeTab === "Summary") {
      headers = ["Month", "Mandates Created", "Active Mandates", "Success Rate (%)"];
      rows = monthly.map((m) => [m.month, m.created, m.active, m.rate]);
      rows.push([]);
      rows.push(["Summary Metric", "Value"]);
      rows.push(["Total Mandates", summary.total]);
      rows.push(["Activated Mandates", summary.activated]);
      rows.push(["Active Value (INR)", summary.value]);
      rows.push(["Overall Success Rate (%)", `${summary.successRate}%`]);
    } else if (activeTab === "By Retailer") {
      headers = [
        "Retailer ID",
        "Shop/Business Name",
        "Owner Name",
        "Total Mandates",
        "Active Mandates",
        "Pending Auth",
        "Failed / Rejected",
        "Success Rate (%)",
        "Total Value (INR)",
      ];
      rows = sortedRetailers.map((r) => [
        r.id,
        r.shopName,
        r.name,
        r.total,
        r.activated,
        r.pending,
        r.failedRejected,
        `${r.successRate}%`,
        r.totalValue,
      ]);
    } else if (activeTab === "By Collection") {
      const collRows = collectionRows(dbInstallments, dbRetailers);
      headers = [
        "Retailer ID",
        "Shop/Business Name",
        "Owner Name",
        "Total Installments",
        "Auto Collected",
        "Offline Collected",
        "Failed",
        "Collection Rate (%)",
        "Amount Collected (INR)",
        "Platform Charges (INR)",
      ];
      rows = collRows.map((r) => [
        r.id,
        r.shopName,
        r.name,
        r.total,
        r.collected,
        r.offline,
        r.failed,
        `${r.rate}%`,
        r.amountCollected,
        r.charges,
      ]);
    } else if (activeTab === "By Bank") {
      headers = [
        "Bank Name",
        "Total Volume",
        "Active Mandates",
        "Pending Auth",
        "Failed / Terminated",
        "Success Rate (%)",
      ];
      rows = bankRowsData.map((b) => [
        b.bank,
        b.total,
        b.active,
        b.pending,
        b.failed,
        `${b.successRate}%`,
      ]);
    } else if (activeTab === "By Auth Mode") {
      headers = [
        "Authentication Mode",
        "Total Volume",
        "Active Mandates",
        "Pending Auth",
        "Failed / Terminated",
        "Success Rate (%)",
      ];
      rows = modeRowsData.map((m) => [
        m.mode === "UPI_AUTO_PAY" ? "UPI Autopay" : m.mode,
        m.total,
        m.active,
        m.pending,
        m.failed,
        `${m.successRate}%`,
      ]);
    }

    if (rows.length === 0) {
      toast.error("No data available to export for this view");
      return;
    }

    downloadCsv([headers, ...rows], `${filenamePrefix}.csv`);
    toast.success(`Exported ${activeTab} report as CSV`);
  }

  function exportReportAsPdf() {
    setDownloadOpen(false);

    const reportTitle = `${activeTab} Report`;
    let tableHeaders: string[] = [];
    let tableDataRows: (string | number)[][] = [];

    if (activeTab === "Summary") {
      tableHeaders = ["Month", "Mandates Created", "Active Mandates", "Success Rate"];
      tableDataRows = monthly.map((m) => [m.month, m.created, m.active, `${m.rate}%`]);
    } else if (activeTab === "By Retailer") {
      tableHeaders = ["Retailer ID", "Shop/Business Name", "Total", "Active", "Pending", "Failed", "Success Rate", "Total Value"];
      tableDataRows = sortedRetailers.map((r) => [
        r.id,
        r.shopName,
        r.total,
        r.activated,
        r.pending,
        r.failedRejected,
        `${r.successRate}%`,
        `₹${r.totalValue.toLocaleString("en-IN")}`,
      ]);
    } else if (activeTab === "By Collection") {
      const collRows = collectionRows(dbInstallments, dbRetailers);
      tableHeaders = ["Retailer", "Total Due", "Collected", "Offline", "Failed", "Rate", "Amount", "Charges"];
      tableDataRows = collRows.map((r) => [
        r.shopName,
        r.total,
        r.collected,
        r.offline,
        r.failed,
        `${r.rate}%`,
        `₹${r.amountCollected.toLocaleString("en-IN")}`,
        `₹${r.charges.toLocaleString("en-IN")}`,
      ]);
    } else if (activeTab === "By Bank") {
      tableHeaders = ["Bank Name", "Total Volume", "Active", "Pending", "Failed", "Success Rate"];
      tableDataRows = bankRowsData.map((b) => [
        b.bank,
        b.total,
        b.active,
        b.pending,
        b.failed,
        `${b.successRate}%`,
      ]);
    } else if (activeTab === "By Auth Mode") {
      tableHeaders = ["Authentication Mode", "Total Volume", "Active", "Pending", "Failed", "Success Rate"];
      tableDataRows = modeRowsData.map((m) => [
        m.mode === "UPI_AUTO_PAY" ? "UPI Autopay" : m.mode,
        m.total,
        m.active,
        m.pending,
        m.failed,
        `${m.successRate}%`,
      ]);
    }

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) {
      toast.info("Opening system print dialog...");
      window.print();
      return;
    }

    const rowsHtml = tableDataRows
      .map(
        (r) =>
          `<tr>${r
            .map((c) => `<td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-size: 12px; color: #1e293b;">${c}</td>`)
            .join("")}</tr>`
      )
      .join("");

    const headersHtml = tableHeaders
      .map(
        (h) =>
          `<th style="padding: 10px 12px; text-align: left; background-color: #f8fafc; border-bottom: 2px solid #cbd5e1; font-size: 12px; font-weight: 600; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;">${h}</th>`
      )
      .join("");

    const dateRangeLabel = range === "Custom Range"
      ? `${customFrom || "Start"} to ${customTo || "Present"}`
      : range;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportTitle} - E-Mandate Platform</title>
          <style>
            @media print {
              @page { margin: 15mm; size: landscape; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              color: #0f172a;
              margin: 0;
              padding: 24px;
              background: #fff;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 2px solid #0284c7;
              padding-bottom: 16px;
              margin-bottom: 20px;
            }
            .title {
              font-size: 24px;
              font-weight: 700;
              color: #0f172a;
              margin: 0;
            }
            .subtitle {
              font-size: 13px;
              color: #64748b;
              margin-top: 4px;
            }
            .meta {
              text-align: right;
              font-size: 12px;
              color: #64748b;
            }
            .kpis {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 12px;
              margin-bottom: 24px;
            }
            .kpi {
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 12px 16px;
              background-color: #f8fafc;
            }
            .kpi-label {
              font-size: 11px;
              text-transform: uppercase;
              font-weight: 600;
              color: #64748b;
            }
            .kpi-value {
              font-size: 20px;
              font-weight: 700;
              color: #0f172a;
              margin-top: 4px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 16px;
            }
            .footer {
              margin-top: 32px;
              padding-top: 12px;
              border-top: 1px solid #e2e8f0;
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              color: #94a3b8;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1 class="title">E-Mandate Platform — ${reportTitle}</h1>
              <p class="subtitle">Performance analysis and metrics overview</p>
            </div>
            <div class="meta">
              <p><strong>Period:</strong> ${dateRangeLabel}</p>
              <p><strong>Generated:</strong> ${new Date().toLocaleDateString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
            </div>
          </div>

          <div class="kpis">
            <div class="kpi">
              <div class="kpi-label">Total Mandates</div>
              <div class="kpi-value">${summary.total}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Active Mandates</div>
              <div class="kpi-value">${summary.activated}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Overall Success Rate</div>
              <div class="kpi-value">${summary.successRate}%</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Active Value</div>
              <div class="kpi-value">₹${summary.value.toLocaleString("en-IN")}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>${headersHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="footer">
            <span>Confidential — Internal E-Mandate Administration</span>
            <span>Page 1 of 1</span>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 400);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    toast.success("PDF print preview generated");
  }

  const hasData = filtered.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Reports</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Analyse mandate performance across retailers, banks and authentication modes
          </p>
        </div>
        <div className="relative flex flex-wrap items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRangeOpen((v) => !v);
                setDownloadOpen(false);
              }}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-ink hover:bg-slate-50 cursor-pointer"
            >
              <Calendar className="h-4 w-4 text-ink-muted" />
              {range}
              <ChevronDown className="h-4 w-4 text-ink-muted" />
            </button>
            {rangeOpen && (
              <div className="absolute right-0 z-20 mt-2 w-48 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                {RANGES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRange(r);
                      setRangeOpen(false);
                    }}
                    className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                      range === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                setDownloadOpen((v) => !v);
                setRangeOpen(false);
              }}
            >
              Download Report
            </Button>
            {downloadOpen && (
              <div className="absolute right-0 top-full z-20 mt-2 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                <button
                  type="button"
                  onClick={exportReportAsCsv}
                  className="block w-full whitespace-nowrap px-4 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
                >
                  Export as CSV
                </button>
                <button
                  type="button"
                  onClick={exportReportAsPdf}
                  className="block w-full whitespace-nowrap px-4 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
                >
                  Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {range === "Custom Range" && (
        <div className="mt-4 flex flex-col gap-3 rounded-[10px] border border-line bg-white p-4 shadow-card sm:flex-row sm:items-end">
          <DatePicker label="From Date" value={customFrom} onChange={setCustomFrom} />
          <DatePicker label="To Date" value={customTo} onChange={setCustomTo} />
        </div>
      )}

      {/* Tabs */}
      <div className="mt-5 inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-[10px] border border-line bg-white p-1 shadow-card">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveTab(t)}
            className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === t
                ? "bg-[#1E40AF] text-white"
                : "text-ink-secondary hover:bg-[#F8FAFC] hover:text-[#0F172A]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="mt-5 flex flex-col gap-4">
          <CardSkeleton cards={4} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[60%_40%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <SkeletonBlock className="h-5 w-40" />
              <SkeletonBlock className="mt-5 h-60 w-full" />
            </div>
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <SkeletonBlock className="h-5 w-36" />
              <div className="mt-6 flex justify-center">
                <SkeletonBlock className="h-[180px] w-[180px] rounded-full" />
              </div>
            </div>
          </div>
          <TableSkeleton rows={5} columns={6} />
        </div>
      ) : !hasData ? (
        <div className="mt-5 flex flex-col items-center justify-center rounded-[10px] border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#EFF6FF] text-[#1E40AF]">
            <BarChart3 className="h-8 w-8" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-ink">No data for this period</h3>
          <p className="mt-1.5 max-w-sm text-sm text-ink-secondary">
            Try selecting a different date range.
          </p>
        </div>
      ) : (
        <div className="mt-5">
          {activeTab === "Summary" && (
            <SummaryTab
              total={summary.total}
              activated={summary.activated}
              value={summary.value}
              successRate={summary.successRate}
              weekly={weekly}
              segments={segments}
              monthly={monthly}
            />
          )}
          {activeTab === "By Retailer" && (
            <RetailerTab
              rows={sortedRetailers}
              totals={retailerTotals}
              sortKey={sortKey}
              sortDir={sortDir}
              sortArrow={sortArrow}
              onSort={toggleSort}
              onRowClick={(id) => navigate(`/admin/retailers/${id}`)}
            />
          )}
          {activeTab === "By Collection" && (
            <CollectionTab
              liveInstallments={dbInstallments}
              liveRetailers={dbRetailers}
              onRowClick={(id) => navigate(`/admin/retailers/${id}`)}
            />
          )}
          {activeTab === "By Bank" && <BankTab chart={bankChart} rows={bankBySuccess} />}
          {activeTab === "By Auth Mode" && <AuthModeTab rows={modeRowsData} modes={modeByMode} />}
        </div>
      )}
    </div>
  );
}

/* ---------------- Summary tab ---------------- */

function SummaryTab({
  total,
  activated,
  value,
  successRate,
  weekly,
  segments,
  monthly,
}: {
  total: number;
  activated: number;
  value: number;
  successRate: string;
  weekly: BarPoint[];
  segments: DonutSegment[];
  monthly: { label: string; total: number; activated: number; failed: number; rejected: number; successRate: number }[];
}) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Mandates"
          value={total.toLocaleString("en-IN")}
          icon={FileText}
          iconBg="bg-[#EFF6FF]"
          iconColor="text-[#1E40AF]"
          onClick={() => navigate("/admin/mandates")}
        />
        <StatCard
          label="Successfully Activated"
          value={activated.toLocaleString("en-IN")}
          icon={CheckCircle2}
          iconBg="bg-[#ECFDF5]"
          iconColor="text-[#059669]"
          onClick={() => navigate("/admin/mandates")}
        />
        <StatCard
          label="Total Mandate Value"
          value={formatINR(value)}
          icon={IndianRupee}
          iconBg="bg-[#F0F9FF]"
          iconColor="text-[#0284C7]"
          onClick={() => navigate("/admin/mandates")}
        />
        <StatCard
          label="Success Rate"
          value={`${successRate}%`}
          icon={TrendingUp}
          iconBg="bg-[#EFF6FF]"
          iconColor="text-[#1E40AF]"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[60%_40%]">
        <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
          <h3 className="text-lg font-semibold text-ink">Mandate Volume</h3>
          <p className="text-[13px] text-ink-secondary">Mandates created per week, last 12 weeks</p>
          <div className="mt-4">
            <VolumeBarChart data={weekly} height={280} />
          </div>
        </div>

        <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
          <h3 className="text-lg font-semibold text-ink">Status Distribution</h3>
          <div className="mt-5 flex justify-center">
            <DonutChart
              segments={segments}
              size={180}
              centerValue={total.toLocaleString("en-IN")}
              centerLabel="Total"
            />
          </div>
          <div className="mt-5 flex flex-col gap-2.5">
            {segments.map((s) => (
              <div key={s.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-[13px] text-ink">{STATUS_LABELS[s.label]}</span>
                </div>
                <span className="text-[13px] font-medium text-ink-secondary">
                  {s.value.toLocaleString("en-IN")} · {total ? Math.round((s.value / total) * 100) : 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Monthly Comparison</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead className="bg-page">
              <tr className="border-b border-line">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">Month</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Total Created</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Activated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Terminated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Halted</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((row) => (
                <tr key={row.label} className="h-14 border-b border-line last:border-0">
                  <td className="px-4 text-sm font-medium text-ink">{row.label}</td>
                  <td className="px-4 text-right text-sm text-ink">{row.total}</td>
                  <td className="px-4 text-right text-sm font-medium text-emerald-600">{row.activated}</td>
                  <td className="px-4 text-right text-sm text-red-600">{row.failed}</td>
                  <td className="px-4 text-right text-sm text-red-600">{row.rejected}</td>
                  <td className="px-4">
                    <p className="text-sm font-medium text-ink">{row.successRate}%</p>
                    <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${row.successRate}%`, backgroundColor: successBarColor(row.successRate) }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- By Retailer tab ---------------- */

function RetailerTab({
  rows,
  totals,
  sortKey,
  sortDir,
  sortArrow,
  onSort,
  onRowClick,
}: {
  rows: ReturnType<typeof retailerRows>;
  totals: { total: number; activated: number; pending: number; failedRejected: number; value: number };
  sortKey: RetailerSortKey;
  sortDir: "asc" | "desc";
  sortArrow: (key: RetailerSortKey) => string;
  onSort: (key: RetailerSortKey) => void;
  onRowClick: (id: string) => void;
}) {
  const th = (label: string, key: RetailerSortKey | null, right = false) => (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary ${
        right ? "text-right" : "text-left"
      }`}
    >
      {key ? (
        <button
          type="button"
          onClick={() => onSort(key)}
          className={`inline-flex items-center gap-1 uppercase tracking-wide cursor-pointer ${
            sortKey === key ? "text-primary-600" : "text-ink-secondary hover:text-ink"
          }`}
        >
          {label}
          <span className="text-[11px]">{sortArrow(key)}</span>
        </button>
      ) : (
        label
      )}
    </th>
  );

  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead className="bg-page">
            <tr className="border-b border-line">
              {th("Retailer", "shopName")}
              {th("Total Mandates", "total", true)}
              {th("Activated", "activated", true)}
              {th("Pending", "pending", true)}
              {th("Failed / Halted", "failedRejected", true)}
              {th("Total Value", "totalValue", true)}
              {th("Success Rate", "successRate")}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const color = successBarColor(r.successRate);
              return (
                <tr
                  key={r.id}
                  onClick={() => onRowClick(r.id)}
                  className="h-14 cursor-pointer border-b border-line last:border-0 transition-colors hover:bg-page"
                >
                  <td className="px-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[13px] font-semibold text-primary-500">
                        {initials(r.name)}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-ink">{r.shopName}</p>
                        <p className="text-xs text-ink-secondary">{r.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 text-right text-sm font-medium text-ink">{r.total}</td>
                  <td className="px-4 text-right text-sm font-medium text-emerald-600">{r.activated}</td>
                  <td className="px-4 text-right text-sm text-amber-600">{r.pending}</td>
                  <td className="px-4 text-right text-sm text-red-600">{r.failedRejected}</td>
                  <td className="px-4 text-right text-sm font-medium text-ink">{formatINR(r.totalValue)}</td>
                  <td className="px-4">
                    <p className="text-sm font-medium text-ink">{r.successRate}%</p>
                    <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full" style={{ width: `${r.successRate}%`, backgroundColor: color }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-[#F8FAFC]">
              <td className="px-4 py-3 text-sm font-semibold text-ink">Total</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-ink">{totals.total}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-600">{totals.activated}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-amber-600">{totals.pending}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-red-600">{totals.failedRejected}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-ink">{formatINR(totals.value)}</td>
              <td className="px-4 py-3">
                <p className="text-sm font-semibold text-ink">
                  {totals.total ? Math.round((totals.activated / totals.total) * 100) : 0}%
                </p>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ---------------- By Bank tab ---------------- */

function BankTab({
  chart,
  rows,
}: {
  chart: BankBar[];
  rows: { bank: string; total: number; activated: number; successRate: number }[];
}) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[55%_45%]">
      <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Mandates by Bank</h3>
        <div className="mt-5">
          <BankBarChart data={chart} />
        </div>
      </div>

      <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Success Rate by Bank</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[420px] text-left">
            <thead className="bg-page">
              <tr className="border-b border-line">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">Bank</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Total</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Activated</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const color = successBarColor(r.successRate);
                return (
                  <tr key={r.bank} className="h-14 border-b border-line last:border-0">
                    <td className="px-4 text-sm font-medium text-ink">{r.bank}</td>
                    <td className="px-4 text-right text-sm text-ink">{r.total}</td>
                    <td className="px-4 text-right text-sm font-medium text-emerald-600">{r.activated}</td>
                    <td className="px-4">
                      <p className="text-sm font-medium text-ink">{r.successRate}%</p>
                      <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${r.successRate}%`, backgroundColor: color }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- By Auth Mode tab ---------------- */

function AuthModeTab({
  rows,
  modes,
}: {
  rows: ReturnType<typeof modeRows>;
  modes: (ReturnType<typeof modeRows>[number] | undefined)[];
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {modes.map((m) => {
          const meta = MODE_META.find((x) => x.mode === m?.mode);
          const Icon = meta?.icon ?? FileText;
          return (
            <div key={m?.mode} className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">{MODE_META.find((x) => x.mode === m?.mode)?.label ?? "—"}</span>
                  <span className="text-[30px] font-bold leading-tight text-ink">
                    {m ? m.total.toLocaleString("en-IN") : "0"}
                  </span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#1E40AF]">
                  <Icon className="h-5 w-5" />
                </div>
              </div>
              <p className="mt-3 text-xs text-ink-secondary">
                {m ? `${m.successRate}% success rate` : "No records"}
              </p>
            </div>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-[10px] border border-line bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead className="bg-page">
              <tr className="border-b border-line">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">Auth Mode</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Total Mandates</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Activated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Terminated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Halted</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">Success Rate</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">Avg. Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const color = successBarColor(r.successRate);
                return (
                  <tr key={r.mode} className="h-14 border-b border-line last:border-0">
                    <td className="px-4 text-sm font-medium text-ink">{MODE_META.find((x) => x.mode === r.mode)?.label ?? r.mode}</td>
                    <td className="px-4 text-right text-sm font-medium text-ink">{r.total}</td>
                    <td className="px-4 text-right text-sm font-medium text-emerald-600">{r.activated}</td>
                    <td className="px-4 text-right text-sm text-red-600">{r.failed}</td>
                    <td className="px-4 text-right text-sm text-red-600">{r.rejected}</td>
                    <td className="px-4">
                      <p className="text-sm font-medium text-ink">{r.successRate}%</p>
                      <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${r.successRate}%`, backgroundColor: color }} />
                      </div>
                    </td>
                    <td className="px-4 text-right text-sm font-medium text-ink">{formatINR(r.avgAmount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- By Collection tab ---------------- */

type CollectionSortKey =
  | "shopName"
  | "total"
  | "collected"
  | "offline"
  | "failed"
  | "amountCollected"
  | "rate"
  | "charges";

function CollectionTab({
  liveInstallments,
  liveRetailers,
  onRowClick,
}: {
  liveInstallments: any[];
  liveRetailers: any[];
  onRowClick: (id: string) => void;
}) {
  const [sortKey, setSortKey] = useState<CollectionSortKey>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const rows = useMemo(() => {
    const arr = collectionRows(liveInstallments, liveRetailers);
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [liveInstallments, liveRetailers, sortKey, sortDir]);

  const totals = useMemo(
    () => ({
      total: rows.reduce((s, r) => s + r.total, 0),
      collected: rows.reduce((s, r) => s + r.collected, 0),
      offline: rows.reduce((s, r) => s + r.offline, 0),
      failed: rows.reduce((s, r) => s + r.failed, 0),
      amount: rows.reduce((s, r) => s + r.amountCollected, 0),
      charges: rows.reduce((s, r) => s + r.charges, 0),
    }),
    [rows]
  );

  function toggleSort(key: CollectionSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const th = (label: string, key: CollectionSortKey, right = false) => (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary ${
        right ? "text-right" : "text-left"
      }`}
    >
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide cursor-pointer ${
          sortKey === key ? "text-primary-600" : "text-ink-secondary hover:text-ink"
        }`}
      >
        {label}
        <span className="text-[11px]">{sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}</span>
      </button>
    </th>
  );

  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead className="bg-page">
            <tr className="border-b border-line">
              {th("Retailer", "shopName")}
              {th("Total Installments", "total", true)}
              {th("Collected", "collected", true)}
              {th("Offline", "offline", true)}
              {th("Failed", "failed", true)}
              {th("Amount Collected", "amountCollected", true)}
              {th("Collection Rate", "rate")}
              {th("Charges Earned", "charges", true)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => onRowClick(r.id)}
                className="h-14 cursor-pointer border-b border-line last:border-0 transition-colors hover:bg-page"
              >
                <td className="px-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[13px] font-semibold text-primary-500">
                      {initials(r.name)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-ink">{r.shopName}</p>
                      <p className="text-xs text-ink-secondary">{r.id}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 text-right text-sm font-medium text-ink">{r.total}</td>
                <td className="px-4 text-right text-sm font-medium text-emerald-600">{r.collected}</td>
                <td className="px-4 text-right text-sm font-medium text-[#1E40AF]">{r.offline}</td>
                <td className="px-4 text-right text-sm font-medium text-red-600">{r.failed}</td>
                <td className="px-4 text-right text-sm font-medium text-ink">{formatINR(r.amountCollected)}</td>
                <td className="px-4">
                  <p className="text-sm font-medium text-ink">{r.rate}%</p>
                  <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min(100, r.rate)}%`, backgroundColor: collectionRateColor(r.rate) }}
                    />
                  </div>
                </td>
                <td className="px-4 text-right text-sm font-medium text-ink">{formatINR(r.charges)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-[#F8FAFC]">
              <td className="px-4 py-3 text-sm font-semibold text-ink">Total</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-ink">{totals.total}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-600">{totals.collected}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-[#1E40AF]">{totals.offline}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-red-600">{totals.failed}</td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-ink">{formatINR(totals.amount)}</td>
              <td className="px-4 py-3">
                <p className="text-sm font-semibold text-ink">
                  {totals.total ? Math.round((totals.collected / totals.total) * 100) : 0}%
                </p>
              </td>
              <td className="px-4 py-3 text-right text-sm font-semibold text-ink">{formatINR(totals.charges)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}